/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';

import { CheckoutActionTypes, CheckoutActions } from '../';
import { ACTION_CREATOR_NAME, paymentHelper, userHelper, orderHelper, ExponeaCheckoutSteps, analyticsHelper, errorToBasketErrorModel, logsHelper } from 'helpers';
import { AppState, ErrorAction } from 'store';
import { OrderPayload, OrderCodeModel, PlacedOrderModel, IfcApplicationData } from 'models';
import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import { BasketDetailsModel } from 'models/Basket';
import { initCheckoutPageSaga } from 'store/checkout/actions';
import { api } from 'repositories/api';
import {
    CardSuccessfulPaymentResponseModel,
    PaypalSuccessfulPaymentResponseModel,
    PaymentMethodTypes,
    PaymentTypes,
    PaymentDetailsModel
} from 'models/Payment';
import { PaymentActionTypes, PaymentActions, PaymentState } from 'store/payment';
import { BasketActionTypes, BasketActions } from 'store/basket';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { prepareIFCRequestData, prepareNonFinanceRequestData } from 'helpers/dynamicFormHelpers';
import { loadBasketSaga } from 'store/basket/actions';

const FIND_ORDER_INTERVAL = 2000;
const FIND_ORDER_ATTEMPTS_COUNT = 10;

export interface ProcessOrderAction extends Action<CheckoutActionTypes.PROCESS_ORDER> {
}

export interface ProcessOrderActionStart extends Action<CheckoutActionTypes.PROCESS_ORDER_START> {
}

export interface ProcessOrderActionSuccess extends Action<CheckoutActionTypes.PROCESS_ORDER_SUCCESS> {
}

export interface ProcessOrderActionError extends ErrorAction<CheckoutActionTypes.PROCESS_ORDER_ERROR> {
}

export interface FindLastOrderActionStart extends Action<CheckoutActionTypes.FIND_LAST_ORDER_START> {
}

export interface FindLastOrderActionSuccess extends Action<CheckoutActionTypes.FIND_LAST_ORDER_SUCCESS> {
}

export interface FindLastOrderActionError extends ErrorAction<CheckoutActionTypes.FIND_LAST_ORDER_ERROR> {
}

export interface PlaceOrderActionStart extends Action<CheckoutActionTypes.PLACE_ORDER_START> {
}

export interface PlaceOrderActionSuccess extends Action<CheckoutActionTypes.PLACE_ORDER_SUCCESS> {
}

export interface PlaceOrderActionError extends ErrorAction<CheckoutActionTypes.PLACE_ORDER_ERROR> {
}

export const processOrder: () => ProcessOrderAction = () => ({
    type: CheckoutActionTypes.PROCESS_ORDER
});
processOrder[ACTION_CREATOR_NAME] = 'processOrder';

export type ProcessOrderFunc = (...args: Parameters<typeof processOrder>) => void;

export function* processOrderSaga(): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.PROCESS_ORDER_START
    });
    try {
        yield callBasketSaga(initCheckoutPageSaga);

        // we have to reload basket again, because something can be changed after finance page initialized
        // for example: customer opened one more tab with catalog, added basket item and then switched to payment page and tried to submit finance form
        yield callBasketSaga(loadBasketSaga);

        let state: AppState = yield select();

        const paymentResult = state.paymentState.successfulPaymentResult || paymentHelper.getSuccessfulPaymentResult();

        const isFinanceOrderWithoutDeposit = paymentHelper.isCreditPaymentWithoutDeposit(state.paymentState.paymentDetails);
        const isUnprocessedBasket = state.basketState.basket?.deliveryItemsQuantity! > 0;

        const isUnprocessedIfcBasketWithoutDeposit = isFinanceOrderWithoutDeposit && isUnprocessedBasket;
        const isUnprocessedBasketExists = paymentResult || isUnprocessedIfcBasketWithoutDeposit;

        if (isUnprocessedBasketExists) {
            yield placeOrderProcessing(state, paymentResult);
        } else {
            // basket can be destroyed on this step - after placing order and reload page
            yield processFindingLastOrder(state.basketState.basket?.deliveryItemsQuantity || 0);
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.CLEAR_FINANCE_DATA
        });

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PROCESS_ORDER_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PROCESS_ORDER_ERROR,
            error: errorToBasketErrorModel(error, 'processOrderSaga')
        });

        return false;
    }
}

function* placeOrderProcessing(state: AppState, paymentResult: CardSuccessfulPaymentResponseModel | PaypalSuccessfulPaymentResponseModel | null): Generator<any, void, any> {
    const isFinanceOrder = paymentHelper.isCreditRelatedPaymentType(state.paymentState.paymentDetails.paymentType);
    const isFinanceOrderWithoutDeposit = paymentHelper.isCreditPaymentWithoutDeposit(state.paymentState.paymentDetails);

    try {
        const { basketState, checkoutState, paymentState } = state;
        const basket = basketState.basket!;
        const basketId = basket.guid;
        const paymentDetails = paymentState.paymentDetails;
        const ifcApplicationData = checkoutState.ifcApplicationData;
        const username = basket.user.name;
        const sessionId = basket.session.sessionId;

        if (isFinanceOrderWithoutDeposit) {
            yield placeIlpOrderWithoutDeposit(ifcApplicationData!, basket, paymentState);

            yield setIlpSubmitionDate(username, sessionId);
        } else if (isFinanceOrder) {
            const financeIlpRequestData = prepareIFCRequestData(ifcApplicationData!, basket, paymentState);

            yield updateIlpOrderData(username, basketId, financeIlpRequestData);

            yield setIlpSubmitionDate(username, sessionId);

            yield placeOrder(username, basket, paymentDetails, paymentResult!);
        } else {
            const nonFinanceIlpRequestData = prepareNonFinanceRequestData(basket.user.uid);

            yield updateIlpOrderData(username, basketId, nonFinanceIlpRequestData);

            yield setIlpSubmitionDate(username, sessionId);

            yield placeOrder(username, basket, paymentDetails, paymentResult!);
        }
    } catch (error) {
        const basketError = errorToBasketErrorModel(error, 'processOrder:placeFinanceOrder');

        yield sendAnalyticsFindingOrderError();

        throw basketError;
    }
}

function* setIlpSubmitionDate(username: string, sessionId: string) {
    try {
        // from old code - this api call should not stop application submit process
        yield api.hybris.saveFinanceSubmissionDate(username, sessionId);
    } catch (error) {
        logsHelper.error(error);
    }
}


function* updateIlpOrderData(userName: string, basketId: string, ilpPayload: any): Generator<any, void, any> {
    try {
        yield api.hybris.updateBasketIlpData(userName, basketId, ilpPayload);
    } catch (error) {
        const basketError = errorToBasketErrorModel(error, 'processOrder:updateIlpOrderData');

        yield sendAnalyticsFindingOrderError();

        throw basketError;
    }
}

function* placeIlpOrderWithoutDeposit(ifcApplicationData: IfcApplicationData, basket: BasketDetailsModel, paymentState: PaymentState): Generator<any, void, any> {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.PLACE_ORDER_START
    });

    try {
        const basketId = basket.guid;
        const paymentDetails = paymentState.paymentDetails;
        const username = basket.user.name;

        sendAnalytics(basket, paymentDetails.paymentType);

        yield api.hybris.reserveCart(username, basketId);

        const financeIlpRequestData = prepareIFCRequestData(ifcApplicationData, basket, paymentState);

        yield api.hybris.placeIlpOrderWithoutDeposit(username, basketId, financeIlpRequestData);

        yield put<PaymentActions>({
            type: PaymentActionTypes.REMOVE_PAYMENT_RESULT
        });

        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_BASKET
        });

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PLACE_ORDER_SUCCESS
        });
    } catch (error) {
        const basketError = errorToBasketErrorModel(error, 'processOrder:placeIlpOrderWithoutDeposit');
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PLACE_ORDER_ERROR,
            error: basketError
        });

        throw basketError;
    }
}

function* placeOrder(username: string, basket: BasketDetailsModel, paymentDetails: PaymentDetailsModel, paymentResult: CardSuccessfulPaymentResponseModel | PaypalSuccessfulPaymentResponseModel): Generator<any, void, any> {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.PLACE_ORDER_START
    });

    try {
        sendAnalytics(basket, paymentDetails.paymentType);

        yield api.hybris.reserveCart(username, basket.guid);

        let deposit = paymentHelper.getDeposit(paymentDetails);

        // if billing address hasn't saved after billing address IU implemented (DeliveryDetailsPage) (for exists customers, for example in return to journey flow)
        // getCustomerDetails api returns billing address the same as delivery
        // BUT getBasketDetails api doesn't return any billing address
        // TODO: remove usage of delivery address when getBasketDetails api behavior will be fixed
        const billingAddress = basket.paymentAddress ?? basket.deliveryAddress!;

        let orderPayload = new OrderPayload(billingAddress, paymentDetails.paymentType === PaymentTypes.IFC, userHelper.getCustomerMiddlename());
        if (paymentResult.paymentMethodType === PaymentMethodTypes.Card) {
            orderPayload.fillOrderPayloadForCardPayment(paymentResult as CardSuccessfulPaymentResponseModel, deposit);
        } else {
            orderPayload.fillOrderPayloadForPaypalPayment(paymentResult as PaypalSuccessfulPaymentResponseModel, deposit);
        }

        const placedOrder: PlacedOrderModel = yield api.hybris.placeOrder(username, orderPayload, basket.guid);

        sendAnalyticsPurchase(placedOrder, paymentDetails.paymentType);

        yield put<PaymentActions>({
            type: PaymentActionTypes.REMOVE_PAYMENT_RESULT
        });

        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_BASKET
        });

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PLACE_ORDER_SUCCESS
        });
    } catch (error) {
        const basketError = errorToBasketErrorModel(error, 'processOrder:placeOrder');
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PLACE_ORDER_ERROR,
            error: basketError
        });

        throw basketError;
    }
}

function* processFindingLastOrder(deliveryItemsQuantity: number): Generator<any, void, any> {
    const errorSource = 'processOrder:processFindingLastOrder';
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.FIND_LAST_ORDER_START
    });

    try {
        // if basket contains any deliveryItemsQuantity - this is unprocessed basket (order has not placed yet) or this is a new basket
        if (deliveryItemsQuantity > 0) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'DeliveryItemsQuantity of current basket is more than 0', errorSource);
        }

        const lastOrderCode: OrderCodeModel | null = yield tryToFindLastOrderCode();

        if (lastOrderCode === null) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Last order code does not exist', errorSource);
        }

        yield sendAnalyticsForLastOrder(lastOrderCode);

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.FIND_LAST_ORDER_SUCCESS
        });
    } catch (error) {
        const basketError = errorToBasketErrorModel(error, errorSource);
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.FIND_LAST_ORDER_ERROR,
            error: basketError
        });

        yield sendAnalyticsFindingOrderError();

        throw basketError;
    }
}

async function tryToFindLastOrderCode(): Promise<OrderCodeModel | null> {
    return new Promise(resolve => {
        let findOrderAttemptIndex = 0;

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        const interval = setInterval(async () => {
            findOrderAttemptIndex++;
            if (findOrderAttemptIndex > FIND_ORDER_ATTEMPTS_COUNT) {
                clearInterval(interval);

                return resolve(null);
            }

            try {
                const orderCode = await orderHelper.getLastOrderCode(userHelper.getCurrentUserName()!);
                if (orderCode !== null) {
                    clearInterval(interval);

                    return resolve(orderCode);
                }
                // eslint-disable-next-line no-empty
            } catch { }
        }, FIND_ORDER_INTERVAL);
    });
}

function sendAnalytics(basket: BasketDetailsModel | PlacedOrderModel, paymentType: PaymentTypes): void {
    analyticsHelper.withLogging(() => {
        api.exponea.checkoutStage(ExponeaCheckoutSteps.OrderProcessing, basket, paymentType);
    }, 'placeOrder|processFindingLastOrder');
}

function* sendAnalyticsForLastOrder(lastOrderCode: OrderCodeModel): Generator<any, void, any> {
    yield analyticsHelper.withLoggingGenerator(function* () {
        const lastOrder: PlacedOrderModel = yield api.hybris.getOrder(userHelper.getCurrentUserName()!, lastOrderCode.orderCode);
        api.exponea.checkoutStage(ExponeaCheckoutSteps.OrderProcessing, lastOrder, null);
    }, 'placeOrder|processFindingLastOrder');
}

function sendAnalyticsPurchase(order: PlacedOrderModel, paymentType: PaymentTypes): void {
    analyticsHelper.withLogging(() => {
        api.exponea.purchase(order, paymentType);
    }, 'placeOrder');
}

function* sendAnalyticsFindingOrderError(): Generator<any, void, any> {
    yield analyticsHelper.withLoggingGenerator(function* () {
        // cloned from old code
        const state: AppState = yield select();
        if (state.basketState.basket) {
            const paymentType = state.paymentState.paymentDetails.paymentType === PaymentTypes.None ? null : state.paymentState.paymentDetails.paymentType;
            api.exponea.checkoutStage(ExponeaCheckoutSteps.OrderProcessing, state.basketState.basket, paymentType);
        }
    }, 'processFindingLastOrder');
}